import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import { Filter, X } from "lucide-react";

const TYPES = ["all", "shirts", "tshirts", "trousers", "jackets"];
const PAGE_SIZE = 8;

export default function Products() {
  const { gender, type } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [priceRange, setPriceRange] = useState([0, 30000]);
  const [activePrice, setActivePrice] = useState([0, 30000]);
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [activeType, setActiveType] = useState(type || params.get("type") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [quick, setQuick] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
  setActiveType(type || params.get("type") || "all");
}, [type, params]);

  const genderLabel = gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : "All";

  const buildQuery = useCallback((nextSkip = 0) => {
    const p = { skip: nextSkip, limit: PAGE_SIZE };
    if (gender && gender !== "all") p.gender = gender;
    if (activeType && activeType !== "all") p.product_type = activeType;
    if (activePrice[0] > 0) p.min_price = activePrice[0];
    if (activePrice[1] < 30000) p.max_price = activePrice[1];
    if (sort) p.sort = sort;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [gender, activeType, activePrice, sort, q]);

  // Initial / filter-change load — reset
  useEffect(() => {
    setLoading(true);
    setSkip(0);
    api.get("/products", { params: buildQuery(0) }).then(({ data }) => {
      setProducts(data.items || []);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
    }).finally(() => setLoading(false));
  }, [buildQuery]);

  const loadMore = async () => {
    setLoadingMore(true);
    const nextSkip = skip + PAGE_SIZE;
    try {
      const { data } = await api.get("/products", { params: buildQuery(nextSkip) });
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const fresh = (data.items || []).filter((p) => !seen.has(p.id));
        return [...prev, ...fresh];
      });
      setSkip(nextSkip);
      setHasMore(data.has_more || false);
    } finally {
      setLoadingMore(false);
    }
  };

  const heroImg = useMemo(() => {
    if (gender === "women") return "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/8a3ea759775d7f37959ae978ac021fb4066d9f70c5f796c3ace720923239f8d3.png";
    if (gender === "men") return "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/1d33ebc43675bab2beee1cd33674c5c3efdea835ac89705b4d5b30ec3a6ce26d.png";
    return "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/0cc1222e34152a05e322999d1d2dde46262335e5d52f33561d890327db0dc37a.png";
  }, [gender]);

  const resetFilters = () => {
    setActiveType("all"); setActivePrice([0, 30000]); setPriceRange([0, 30000]);
    setQ(""); navigate("/shop");
  };

  return (
    <div data-testid="products-page">
      {/* Header banner */}
      <section className="relative h-[36vh] sm:h-[42vh] min-h-[260px] overflow-hidden">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/30"/>
        <div className="relative h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-10 sm:pb-12 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <div className="ie-overline opacity-80 mb-3">The Atelier · {genderLabel}</div>
            <h1 className="font-serif text-[40px] sm:text-5xl md:text-7xl tracking-tight leading-[0.95]">
              {q ? `Searching "${q}"` : gender === "women" ? "Composed for her" : gender === "men" ? "Tailored, never tight" : "The full atelier"}
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
        {/* Filters */}
        <aside className={`md:col-span-3 ${filtersOpen ? "fixed inset-0 z-[60] bg-background overflow-y-auto p-6 md:static md:p-0 md:overflow-visible md:z-auto" : "hidden md:block"}`} data-testid="filters-sidebar">
          <div className="flex items-center justify-between mb-8 md:hidden">
            <div className="ie-overline">Filters</div>
            <button onClick={() => setFiltersOpen(false)} aria-label="close filters" data-testid="filters-close"><X className="w-5 h-5"/></button>
          </div>

          <div className="md:sticky md:top-24 space-y-10">
            <div>
              <div className="ie-overline mb-4">Category</div>
              <ul className="space-y-2.5">
                {TYPES.map((t) => (
                  <li key={t}>
                    <button
                      onClick={() => setActiveType(t)}
                      className={`text-sm transition-colors ${activeType === t ? "text-brand-accent" : "text-text-secondary hover:text-foreground"}`}
                      data-testid={`filter-type-${t}`}
                    >
                      {t === "all" ? "All categories" : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="ie-overline mb-4">Price</div>
              <Slider
                value={priceRange}
                min={0}
                max={30000}
                step={500}
                onValueChange={(v) => setPriceRange(v)}
                onValueCommit={(v) => setActivePrice(v)}
                className="my-5"
                data-testid="filter-price-slider"
              />
              <div className="flex justify-between text-xs text-text-secondary">
                <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
                <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
              </div>
            </div>

            <div>
              <div className="ie-overline mb-4">Gender</div>
              <ul className="space-y-2.5">
                {[["All", "/shop"], ["Women", "/shop/women"], ["Men", "/shop/men"]].map(([label, href]) => (
                  <li key={label}>
                    <button
                      onClick={() => navigate(href)}
                      className={`text-sm transition-colors ${(!gender && label === "All") || gender === label.toLowerCase() ? "text-brand-accent" : "text-text-secondary hover:text-foreground"}`}
                      data-testid={`filter-gender-${label.toLowerCase()}`}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {(activeType !== "all" || activePrice[0] > 0 || activePrice[1] < 30000 || q) && (
              <button onClick={resetFilters} className="ie-overline text-brand-accent hover:underline" data-testid="filters-reset">
                Reset filters
              </button>
            )}

            {filtersOpen && (
              <button onClick={() => setFiltersOpen(false)} className="ie-btn-primary w-full justify-center md:hidden" data-testid="filters-apply">
                Show {total} editions
              </button>
            )}
          </div>
        </aside>

        {/* Products */}
        <main className="md:col-span-9">
          <div className="flex items-center justify-between border-b border-border pb-4 mb-6 md:mb-8 gap-3">
            <div className="text-xs sm:text-sm text-text-secondary truncate" data-testid="results-count">
              {loading ? "Loading…" : `${total} ${total === 1 ? "edition" : "editions"}`}
              {q && <span className="ml-2 italic">· "{q}"</span>}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <button onClick={() => setFiltersOpen(true)} className="md:hidden ie-overline inline-flex items-center gap-1.5" data-testid="filters-open">
                <Filter className="w-3.5 h-3.5"/> Filters
              </button>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent border-0 ie-overline outline-none cursor-pointer text-right pr-1"
                data-testid="sort-select"
              >
                <option value="newest">Newest</option>
                <option value="price_asc">Price · Low</option>
                <option value="price_desc">Price · High</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center">
              <div className="font-serif text-3xl mb-3">Nothing quite fits.</div>
              <div className="text-text-secondary text-sm mb-8">Try widening your filters.</div>
              <button onClick={resetFilters} className="ie-btn-ghost" data-testid="clear-filters">
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6" data-testid="products-grid">
                <AnimatePresence>
                  {products.map((p, i) => (
                    <motion.div
                      key={`grid-${p.id}`}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: (i % PAGE_SIZE) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <ProductCard product={p} onQuickView={setQuick}/>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {hasMore && (
                <div className="flex justify-center mt-12 md:mt-16">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="ie-btn-ghost px-12"
                    data-testid="load-more"
                  >
                    {loadingMore ? "Loading…" : `Load More · ${total - products.length} remaining`}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <QuickView product={quick} open={!!quick} onClose={() => setQuick(null)} />
    </div>
  );
}
